import { useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { Alert, Button, Skeleton, Tag } from 'antd'
import { WorkspaceHeader } from '@/components/workspace-header.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { cn } from '@/utils/index.ts'
import { RentalEnterpriseEditor } from './rental-enterprise-editor.tsx'

export function RentalBatchEnterprisePage({ ids }: { ids: number[] }) {
  const trpc = useTRPC()
  const queries = useQueries({
    queries: ids.map((rentalOrderId) => trpc.order.rental.detail.queryOptions({ rentalOrderId }))
  })
  const [activeId, setActiveId] = useState(ids[0])
  const [visited, setVisited] = useState(() => new Set([ids[0]]))
  const [revisions, setRevisions] = useState<Record<number, number>>({})
  const [busy, setBusy] = useState(false)
  const orders = queries.flatMap((query) => (query.data ? [query.data] : []))
  const missingCount = orders.filter((order) => order.missingContractData.length > 0).length
  return (
    <div className="bg-page flex h-dvh min-h-0 flex-col overflow-hidden">
      <WorkspaceHeader>
        <div>
          <h1 className="m-0 text-base font-semibold">批量补齐资料</h1>
          <span className="text-content-muted text-xs">
            {ids.length} 个订单 · {missingCount} 个待补齐 · 逐单保存
          </span>
        </div>
      </WorkspaceHeader>
      <main className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-3 overflow-hidden p-3 lg:grid-cols-[18rem_minmax(0,1fr)] lg:grid-rows-1">
        <nav aria-label="待补齐资料订单" className="max-h-56 min-w-0 space-y-3 overflow-auto lg:max-h-none">
          {ids.map((id, index) => {
            const query = queries[index]
            const order = query.data
            return (
              <button
                key={id}
                type="button"
                disabled={busy}
                aria-current={id === activeId ? 'true' : undefined}
                onClick={() => {
                  setActiveId(id)
                  setVisited((previous) => new Set(previous).add(id))
                }}
                className={cn(
                  'bg-surface border-border flex w-full cursor-pointer flex-col gap-2 rounded-xl border p-4 text-left disabled:cursor-not-allowed disabled:opacity-60',
                  id === activeId && 'border-accent/50 bg-accent/10'
                )}
              >
                <span className="text-content-muted text-xs">
                  订单 {index + 1} · #{id}
                </span>
                <span className="text-sm font-semibold">{order?.orderNo ?? '正在加载'}</span>
                <span className="text-content-muted text-xs">{order?.customerName}</span>
                {order && (
                  <Tag className="self-start" color={order.missingContractData.length ? 'warning' : 'success'}>
                    {order.missingContractData.length ? `待补齐 ${order.missingContractData.length} 项` : '资料齐全'}
                  </Tag>
                )}
                {query.error && <span className="text-danger text-xs">加载失败</span>}
              </button>
            )
          })}
        </nav>
        <section className="bg-surface border-border min-h-0 min-w-0 overflow-hidden rounded-xl border">
          {[...visited].map((id) => {
            const query = queries[ids.indexOf(id)]
            const order = query.data
            return (
              <div
                key={id}
                hidden={id !== activeId}
                className={id === activeId ? 'flex h-full min-h-0 flex-col' : 'hidden'}
              >
                {query.isPending && <Skeleton active className="p-6" />}
                {query.error && (
                  <Alert
                    className="m-6"
                    type="error"
                    title={query.error.message}
                    action={<Button onClick={() => void query.refetch()}>重试</Button>}
                  />
                )}
                {order && (
                  <>
                    <header className="border-border flex shrink-0 flex-wrap items-center justify-between gap-3 border-b px-6 py-4">
                      <div>
                        <h2 className="m-0 text-base font-semibold">{order.orderNo}</h2>
                        <p className="text-content-muted mt-1 mb-0 text-xs">{order.customerName} · 服务对象及资料</p>
                      </div>
                      <Tag color={order.missingContractData.length ? 'warning' : 'success'}>
                        {order.missingContractData.length
                          ? `待补齐 ${order.missingContractData.length} 项`
                          : '资料齐全'}
                      </Tag>
                    </header>
                    <div className="flex min-h-0 flex-1 flex-col gap-4 p-6">
                      {order.missingContractData.length > 0 && (
                        <Alert
                          className="shrink-0"
                          showIcon
                          type="warning"
                          title={`待补充：${order.missingContractData.join('、')}`}
                        />
                      )}
                      <RentalEnterpriseEditor
                        key={`${id}:${revisions[id] ?? 0}`}
                        order={order}
                        onBusyChange={setBusy}
                        onSaved={() => setRevisions((previous) => ({ ...previous, [id]: (previous[id] ?? 0) + 1 }))}
                      />
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </section>
      </main>
    </div>
  )
}
